import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { useMapsGoogleConfigurationQuery, useOffersSettingsQuery } from 'api/queries/config';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ControlledField,
  DEFAULT_MAX_ADVANCE_TIME,
  DateTimePickerInputField,
  LanguageField,
  PassengerCountField,
  RideOfferType,
  SelectInputField,
  StartLocationField,
  getDateTimeShortFormatWithLocale,
  DEFAULT_MAX_RANGE_PASSENGER_COUNT,
} from 'remote/global';
import { CityHireRideSchemaType, RideDetailsSchemaType, cityHireRideSchema } from '../../types';
// eslint-disable-next-line import/no-cycle
import { RideOffersFormSubmitButton, TravelTypeTabsContainer } from '../CreateRideOffersForm';
import VehicleSelectField from '../fields/VehicleSelectField';

function VehicleCityHireRideStepForm({
  defaultValues,
  onValid,
  setFormResult,
}: {
  defaultValues?: Partial<RideDetailsSchemaType>;
  onValid: (data: CityHireRideSchemaType) => void;
  setFormResult: React.Dispatch<React.SetStateAction<Partial<RideDetailsSchemaType> | undefined>>;
}) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const { data: offersSettings } = useOffersSettingsQuery();
  const { cityHireDuration } = offersSettings ?? {};
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const maxAdvanceTime = DEFAULT_MAX_ADVANCE_TIME;
  const formInstance = useForm<CityHireRideSchemaType>({
    defaultValues: {
      startLocation: undefined,
      endLocation: undefined,
      startTime: '',
      maxStartTime: maxAdvanceTime,
      driverLanguage: undefined,
      maxPassengerCount: DEFAULT_MAX_RANGE_PASSENGER_COUNT,
      licensePlate: '',
      passengerCount: '' as any,
      duration: '' as any,
      clientId: '' as any,
      ...defaultValues,
      type: RideOfferType.CITY_HIRE,
    },
    resolver: zodResolver(cityHireRideSchema),
  });
  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  const durationOptions = Object.entries(cityHireDuration ?? {}).map(([minutes]) => {
    return {
      label: t('MultiStop.labels.hourWithCount', { count: Math.floor(Number(minutes) / 60) }),
      value: minutes,
    };
  });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid item container gap={2}>
        <TravelTypeTabsContainer
          type={RideOfferType.CITY_HIRE}
          setFormResult={setFormResult}
          getValues={getValues}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StartLocationField form={formInstance} googleConfig={googleConfig!} />
            <ControlledField
              control={control}
              name='startTime'
              render={(renderProps) => (
                <DateTimePickerInputField
                  {...renderProps}
                  fieldProps={{
                    disablePast: true,
                    maxDateTime: dayjs(maxAdvanceTime),
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
            <ControlledField
              control={control}
              name='duration'
              render={(renderProps) => (
                <SelectInputField {...renderProps} options={durationOptions} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PassengerCountField form={formInstance} />
            <VehicleSelectField form={formInstance} />
            <LanguageField form={formInstance} name='driverLanguage' />
          </Grid>
        </Grid>
        <RideOffersFormSubmitButton
          isSubmitting={isSubmitting}
          isValidating={isValidating}
          isLoading={isLoading}
        />
      </Grid>
    </form>
  );
}

export default VehicleCityHireRideStepForm;
